import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import queryString from "query-string";
import { Link } from "react-router-dom";
import httpService from "../../../services/http.service";
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import AppPagination from "../../Shared/AppPagination";
import KpiCard from "../../Shared/KpiCard";
import { ShimmerTableRows } from "../../Shared/Shimmer";
import MyOpinionLogo from "../../../assets/images/MyOpinion_Logo_Head.png";

const PENDING = 'Pending';
const SUCCESS = 'Success';
const FAILED = 'Failed';

const STATUS = {
    [PENDING]: { label: "En attente", badge: 'badge-warning' },
    [SUCCESS]: { label: "Validée", badge: 'badge-success' },
    [FAILED]: { label: "Refusée", badge: 'badge-danger' },
};

/**
 * Validation d'identité (KYC) : file des demandes de vérification de profil, filtrées par statut.
 * L'examen d'une demande (pièces, validation, refus avec motif) se fait sur sa page de détails.
 */
const VerificationDemandList = ({ history, location }) => {
    const { t, i18n } = useTranslation();

    const query = queryString.parse(location.search);
    const statut = [PENDING, SUCCESS, FAILED].includes(query.statut) ? query.statut : '';
    const page = parseInt(query.page || 1, 10);

    const [fetching, setFetching] = useState(true);
    const [verifications, setVerifications] = useState([]);
    const [counts, setCounts] = useState(null);
    const [pagination, setPagination] = useState({ currentPage: page, lastPage: 1, hasNext: false, hasPrevious: false, totalItems: 0 });

    // Compteurs par statut (une requête légère par statut : seul `meta.total` est lu).
    useEffect(() => {
        let cancelled = false;
        Promise.allSettled([PENDING, SUCCESS, FAILED].map((status) => httpService.get('/verifications', { params: { statut: status, page: 1 } })))
            .then((results) => {
                if (cancelled) return;
                const total = (index) => (results[index].status === 'fulfilled' ? results[index].value?.data?.meta?.total || 0 : 0);
                setCounts({ [PENDING]: total(0), [SUCCESS]: total(1), [FAILED]: total(2) });
            });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setFetching(true);
        httpService.get('/verifications', { params: { page, ...(statut ? { statut } : {}) } })
            .then(({ data: response }) => {
                if (cancelled) return;
                setVerifications(response.data || []);
                setPagination({
                    currentPage: response?.meta?.current_page || 1,
                    lastPage: response?.meta?.last_page || 1,
                    totalItems: response?.meta?.total || 0,
                    hasNext: !!response?.links?.next,
                    hasPrevious: !!response?.links?.prev,
                });
            })
            .catch(() => !cancelled && setVerifications([]))
            .finally(() => !cancelled && setFetching(false));
        return () => { cancelled = true; };
    }, [page, statut]);

    const goTo = (params) => history.push(`/verifications?${queryString.stringify(params)}`);
    const filterBy = (value) => goTo(value ? { statut: value } : {});
    const handlePaginate = (direction) => goTo({ ...(statut ? { statut } : {}), page: direction === 'previous' ? page - 1 : page + 1 });

    const formatDate = (value) => {
        if (!value) return '-';
        const date = new Date(String(value).replace(' ', 'T'));
        return Number.isNaN(date.getTime()) ? value : date.toLocaleString(i18n.language === 'en' ? 'en-GB' : 'fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
    };

    const allCount = counts ? counts[PENDING] + counts[SUCCESS] + counts[FAILED] : null;
    const tabs = [
        { value: '', label: t('Toutes'), count: allCount },
        { value: PENDING, label: t('En attente'), count: counts && counts[PENDING] },
        { value: SUCCESS, label: t('Validées'), count: counts && counts[SUCCESS] },
        { value: FAILED, label: t('Refusées'), count: counts && counts[FAILED] },
    ];

    return (
        <div className="container-fluid">
            <AppBreadcrumb title={t("Validation d'identité")} />

            <div className="row clearfix equal-height-row">
                <div className="col-lg-4 col-md-6 col-12">
                    <KpiCard icon="fa-hourglass-half" tone="orange" label={t('En attente')} loading={!counts}
                        value={counts ? counts[PENDING] : ''} hint={t("Demandes à examiner")} />
                </div>
                <div className="col-lg-4 col-md-6 col-12">
                    <KpiCard icon="fa-check" tone="green" label={t('Validées')} loading={!counts}
                        value={counts ? counts[SUCCESS] : ''} hint={t("Identités confirmées")} />
                </div>
                <div className="col-lg-4 col-md-12 col-12">
                    <KpiCard icon="fa-times" tone="purple" label={t('Refusées')} loading={!counts}
                        value={counts ? counts[FAILED] : ''} hint={t("Demandes rejetées avec motif")} />
                </div>
            </div>

            <ul className="nav stats-tabs" role="tablist">
                {tabs.map((tab) => (
                    <li className="nav-item" key={tab.value || 'all'}>
                        <button type="button" role="tab" aria-selected={statut === tab.value}
                            className={`nav-link btn btn-link ${statut === tab.value ? 'active' : ''}`} onClick={() => filterBy(tab.value)}>
                            {tab.label}
                            {tab.count !== null && tab.count !== undefined && <span className="badge badge-pill badge-light ml-2">{tab.count}</span>}
                        </button>
                    </li>
                ))}
            </ul>

            <div className="card">
                <div className="table-responsive">
                    <table className="table table-hover mb-0 verification-table">
                        <thead>
                            <tr>
                                <th>{t("Photo")}</th>
                                <th>{t("Utilisateur")}</th>
                                <th>{t("Pays")}</th>
                                <th>{t("Date de soumission")}</th>
                                <th>{t("Statut")}</th>
                                <th className="text-right"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {fetching ? (
                                <ShimmerTableRows rows={6} columns={6} />
                            ) : verifications.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="text-center text-muted py-5">
                                        <i className="fa fa-id-card-o fa-2x d-block mb-2" aria-hidden="true" />
                                        {t("Aucune demande de validation d'identité.")}
                                    </td>
                                </tr>
                            ) : verifications.map((verification) => {
                                const user = verification?.user || {};
                                const status = STATUS[verification?.verif_demand_status];
                                return (
                                    <tr key={verification?.verif_demand_id}>
                                        <td className="w60">
                                            <img src={user?.profile?.prof_picture || MyOpinionLogo} alt="" className="w35 rounded" style={{ objectFit: 'cover' }} />
                                        </td>
                                        <td>
                                            <strong>{`${user.user_surname || ''} ${user.user_name || ''}`.trim() || '-'}</strong>
                                            <div className="text-muted small">@{user.user_username}</div>
                                        </td>
                                        <td>{user.user_ip_pays || '-'}</td>
                                        <td>{formatDate(verification?.verif_demand_submitted_at)}</td>
                                        <td>{status && <span className={`badge ${status.badge}`}>{t(status.label)}</span>}</td>
                                        <td className="text-right">
                                            <Link to={`/verifications/${verification?.verif_demand_id}/details`}
                                                className={`btn btn-sm font-weight-bold ${verification?.verif_demand_status === PENDING ? 'btn-primary' : 'btn-outline-info'}`}>
                                                <i className="fa fa-eye" aria-hidden="true" />{' '}
                                                {verification?.verif_demand_status === PENDING ? t('Examiner') : t('Consulter')}
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="body">
                    <AppPagination
                        onNavigate={handlePaginate}
                        currentPage={pagination.currentPage}
                        hasNext={pagination.hasNext}
                        hasPrevious={pagination.hasPrevious}
                        totalItems={pagination.totalItems}
                        lastPage={pagination.lastPage}
                    />
                </div>
            </div>
        </div>
    );
};

export default VerificationDemandList;
